import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

export interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
  agreeToTerms: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
}

interface CustomerInfoFormProps {
  onSubmit: (data: CustomerFormData) => void;
  defaultValues?: Partial<CustomerFormData>;
  isSubmitting?: boolean;
}

const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({ onSubmit, defaultValues, isSubmitting = false }) => {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    firstName: yup.string().required(t('customerForm.validation.firstNameRequired')),
    lastName: yup.string().required(t('customerForm.validation.lastNameRequired')),
    email: yup
      .string()
      .email(t('customerForm.validation.emailInvalid'))
      .required(t('customerForm.validation.emailRequired')),
    phone: yup
      .string()
      .required(t('customerForm.validation.phoneRequired'))
      .matches(/^[0-9]{10}$/, t('customerForm.validation.phoneDigits')),
    notes: yup.string().max(500, t('customerForm.validation.notesMax')),
    agreeToTerms: yup.boolean().oneOf([true], t('customerForm.validation.termsRequired')),
    emailNotifications: yup.boolean(),
    smsNotifications: yup.boolean(),
  });

  const { control, handleSubmit, formState: { errors } } = useForm<CustomerFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      email: defaultValues?.email || '',
      phone: defaultValues?.phone || '',
      notes: defaultValues?.notes || '',
      agreeToTerms: defaultValues?.agreeToTerms || false,
      emailNotifications: defaultValues?.emailNotifications || true,
      smsNotifications: defaultValues?.smsNotifications || false,
    },
  });

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        {t('customerForm.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('customerForm.subtitle')}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit as any)}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('customerForm.firstName')}
                      variant="outlined"
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      required
                    />
                  )}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('customerForm.lastName')}
                      variant="outlined"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      required
                    />
                  )}
                />
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('customerForm.email')}
                      variant="outlined"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      required
                      type="email"
                    />
                  )}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('customerForm.phone')}
                      variant="outlined"
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone?.message || t('customerForm.phoneHelper')}
                      required
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                  )}
                />
              </Box>
            </Stack>

            <Box>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('customerForm.notes')}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    error={!!errors.notes}
                    helperText={errors.notes?.message || t('customerForm.notesHelper')}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('customerForm.notifications')}
              </Typography>
              <Controller
                name="emailNotifications"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={field.onChange}
                        name="emailNotifications"
                      />
                    }
                    label={t('customerForm.emailNotif')}
                  />
                )}
              />
            </Box>


            <Box>
              <Controller
                name="agreeToTerms"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={field.onChange}
                        name="agreeToTerms"
                      />
                    }
                    label={
                      <span>
                        J'accepte les{' '}
                        <Link
                          to="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', fontWeight: 600 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          conditions générales
                        </Link>
                      </span>
                    }
                  />
                )}
              />
              {errors.agreeToTerms && (
                <Typography color="error" variant="caption" display="block">
                  {errors.agreeToTerms.message}
                </Typography>
              )}
            </Box>

            <Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 2, color: 'text.primary' }}
                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {isSubmitting ? t('booking.processing') : t('customerForm.completeBooking')}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default CustomerInfoForm;
